import { ChevronDownIcon } from "@radix-ui/react-icons";
import {
  Children,
  isValidElement,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { useIntl } from "react-intl";
import type { Components } from "react-markdown";
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from "~/components/ui/popover";
import { extractReferenceContext } from "~/utils/markdownTerms";
import {
  CITATION_ANCHOR_SELECTOR,
  CITATION_GROUP_CLASS,
} from "./rehypeGroupSourceCitations";
import { useReportContainer } from "./reportContainerContext";

/** Extract the visible page label (e.g. "1", "R-1") from a citation element. */
function getCitationLabel(citation: ReactElement): string {
  const props = citation.props as Record<string, unknown>;
  if (typeof props.children === "string") {
    return props.children;
  }
  return "";
}

/** Extract the title (document name) from a citation React element. */
function getCitationTitle(citation: ReactElement): string {
  const props = citation.props as Record<string, unknown>;
  if (typeof props.title === "string" && props.title) {
    return props.title;
  }
  return getCitationLabel(citation);
}

/**
 * Number of citation rows that stay visible before the dropdown scrolls.
 */
const MAX_VISIBLE_CITATIONS = 10;

/**
 * Rendered height (px) of a single citation row (px-2 py-1.5 padding + badge
 * line height). Used to cap the dropdown so that at most
 * MAX_VISIBLE_CITATIONS rows fit before a vertical scrollbar appears.
 */
const CITATION_ROW_HEIGHT_PX = 32;

interface ReportSourceCitationGroupProps {
  children?: ReactNode;
}

/**
 * Collapses a run of consecutive inline citation badges into a single
 * clickable trigger (count + chevron) that opens a dropdown/popover listing
 * every badge.
 *
 * The badges themselves are the original `source-map-link` / `bates-link`
 * anchors, so clicking one inside the popover still opens the source modal via
 * the existing report-level click delegation. The popover content is portaled
 * into the report container (see {@link useReportContainer}) so the anchors
 * stay inside the delegation root while keeping the DOM valid (no block
 * element nested in the `<p>`). It closes on click outside / Escape (Radix
 * Popover) as well as when a badge is clicked.
 *
 * Because the badges are portaled out of their paragraph, their surrounding
 * prose is no longer reachable from the anchor. The trigger button stays at the
 * run's original in-paragraph position, so on open we derive the reference
 * context from it and expose it on the popover content via
 * `data-reference-context`; the click-delegation handler then reads it back
 * through {@link extractReferenceContext}, preserving the source heatmap.
 */
export const ReportSourceCitationGroup = ({
  children,
}: ReportSourceCitationGroupProps) => {
  const { formatMessage } = useIntl();
  const reportContainer = useReportContainer();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [referenceContext, setReferenceContext] = useState("");

  const citations = Children.toArray(children).filter(isValidElement);

  if (citations.length === 0) {
    return null;
  }

  const handleOpenChange = (next: boolean) => {
    if (next && triggerRef.current) {
      setReferenceContext(extractReferenceContext(triggerRef.current));
    }
    setOpen(next);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          aria-label={formatMessage(
            { id: "report.citations.show" },
            { count: citations.length }
          )}
          className="group ml-[2px] inline-flex h-[1.4em] cursor-pointer items-center gap-0.5 align-middle text-[0.7em] font-normal leading-none transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring relative top-[-0.05em]"
        >
          <span className="inline-flex h-[1.4em] items-center rounded-full bg-muted-foreground/20 px-[0.45em] leading-none text-muted-foreground hover:bg-muted-foreground/30">
            <ChevronDownIcon className="size-[1em] shrink-0" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverPortal container={reportContainer ?? undefined}>
        <PopoverContent
          align="start"
          className="w-auto min-w-32 max-w-xs p-1"
          data-reference-context={referenceContext || undefined}
          onClick={() => {
            setOpen(false);
          }}
        >
          <div
            className="overflow-y-auto"
            style={{
              maxHeight: MAX_VISIBLE_CITATIONS * CITATION_ROW_HEIGHT_PX,
            }}
          >
            {citations.map((citation, index) => {
              const title = getCitationTitle(citation as ReactElement);
              return (
                <div
                  key={index}
                  role="button"
                  tabIndex={0}
                  className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors hover:bg-accent"
                  onClick={(e) => {
                    const anchor = (
                      e.currentTarget as HTMLElement
                    ).querySelector(
                      CITATION_ANCHOR_SELECTOR
                    ) as HTMLElement | null;
                    if (
                      anchor &&
                      e.target !== anchor &&
                      !anchor.contains(e.target as Node)
                    ) {
                      anchor.click();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      const anchor = (
                        e.currentTarget as HTMLElement
                      ).querySelector(
                        CITATION_ANCHOR_SELECTOR
                      ) as HTMLElement | null;
                      anchor?.click();
                    }
                  }}
                >
                  <span className="sr-only">{citation}</span>
                  <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-muted-foreground/20 px-1.5 text-xs leading-none text-muted-foreground">
                    {getCitationLabel(citation as ReactElement)}
                  </span>
                  <span className="truncate">{title}</span>
                </div>
              );
            })}
          </div>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
};

/**
 * react-markdown component overrides that render collapsed citation groups.
 *
 * The grouping rehype plugin wraps consecutive citation anchors in a
 * `<span class="report-citation-group">`; this override detects that marker and
 * renders the dropdown, while every other span renders normally.
 */
export const reportCitationComponents: Components = {
  span: ({ node, className, children, ...props }) => {
    const classes = typeof className === "string" ? className.split(/\s+/) : [];
    if (classes.includes(CITATION_GROUP_CLASS)) {
      return <ReportSourceCitationGroup>{children}</ReportSourceCitationGroup>;
    }
    return (
      <span className={className} {...props}>
        {children}
      </span>
    );
  },
};
